<template>
    <!-- 页眉 -->
    <header-leafly />

    <!-- Hero Section - 紫罗兰玻璃主视觉 -->
    <section class="violet-hero">
        <div class="hero-overlay"></div>
        <div class="container mx-auto px-4 h-full flex items-center relative z-10">
            <div class="max-w-3xl text-white" data-aos="fade-up">
                <h1 class="text-5xl md:text-6xl mb-6 leading-tight">
                    Discover Violet Glass
                </h1>
                <p class="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
                    Nature's most powerful protection for premium products. Experience the science of biophotonic glass technology.
                </p>
                <button 
                    class="bg-violet-600 hover:bg-violet-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
                    @click="scrollToCollection"
                >
                    Explore Collection
                </button>
            </div>
        </div>
    </section>

    <!-- 科学原理介绍 -->
    <section class="py-20 bg-gradient-to-b from-white to-gray-50">
        <div class="container mx-auto px-4">
            <div class="text-center mb-16" data-aos="fade-up">
                <h2 class="text-4xl md:text-5xl mb-6 text-gray-900">
                    The Science Behind Violet Glass
                </h2>
                <p class="text-xl text-gray-600 max-w-3xl mx-auto">
                    Biophotonic glass filters harmful light while allowing beneficial frequencies to pass through, 
                    preserving and enhancing the molecular structure of your products.
                </p>
            </div>

            <div class="grid md:grid-cols-3 gap-8 mb-16">
                <div class="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow" data-aos="fade-up" data-aos-delay="100">
                    <div class="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg class="w-10 h-10 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h3 class="text-2xl  mb-4 text-gray-900">UV Protection</h3>
                    <p class="text-gray-600 leading-relaxed">
                        Blocks harmful UV and visible light spectrum, preventing degradation and oxidation of sensitive contents.
                    </p>
                </div>

                <div class="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow" data-aos="fade-up" data-aos-delay="200">
                    <div class="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg class="w-10 h-10 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <h3 class="text-2xl mb-4 text-gray-900">Energy Enhancement</h3>
                    <p class="text-gray-600 leading-relaxed">
                        Allows beneficial violet, UVA and infrared light to penetrate, energizing and preserving molecular structure.
                    </p>
                </div>

                <div class="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow" data-aos="fade-up" data-aos-delay="300">
                    <div class="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg class="w-10 h-10 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 class="text-2xl  mb-4 text-gray-900">Extended Freshness</h3>
                    <p class="text-gray-600 leading-relaxed">
                        Significantly extends shelf life and maintains potency, aroma, and quality of natural products.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- 光谱对比图 -->
    <section class="py-20 bg-violet-900 text-white">
        <div class="container mx-auto px-4">
            <div class="grid md:grid-cols-2 gap-12 items-center">
                <div data-aos="fade-right">
                    <h2 class="text-4xl mb-6">Light Spectrum Filtration</h2>
                    <p class="text-lg mb-6 opacity-90">
                        Violet glass acts as a natural filter, blocking the complete spectrum of visible light 
                        with the exception of violet light. This unique property creates an optimal environment 
                        for preserving and revitalizing natural products.
                    </p>
                    <ul class="space-y-4">
                        <li class="flex items-start">
                            <svg class="w-6 h-6 text-violet-300 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-lg">Blocks harmful visible light (400-700nm)</span>
                        </li>
                        <li class="flex items-start">
                            <svg class="w-6 h-6 text-violet-300 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-lg">Permits violet and UV-A light penetration</span>
                        </li>
                        <li class="flex items-start">
                            <svg class="w-6 h-6 text-violet-300 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-lg">Allows infrared radiation for energy transfer</span>
                        </li>
                    </ul>
                </div>
                <div class="relative" data-aos="fade-left">
                    <div class="rounded-3xl shadow-2xl overflow-hidden relative">
                        <video 
                            ref="videoRef"
                            class="w-full h-full object-container"
                            muted 
                            playsinline
                            @ended="onVideoEnded"
                            @play="onVideoPlay"
                        >
                            <source src="https://images.leaflypackaging.com/profile/upload/2026/01/06/violetjar2_20260106222419A002.mp4" type="video/mp4">
                            Your browser does not support the video tag.
                        </video>
                        <!-- 播放按钮覆盖层 -->
                        <div 
                            v-if="showPlayButton" 
                            class="video-play-overlay absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-all hover:bg-black/40 z-50"
                            @click="replayVideo"
                        >
                            <div class="play-button-triangle"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- 视差背景层 -->
    <div class="parallax-background-wrapper" >
        <div class="container mx-auto mt-[68px]" >
            <div style="background: url('/leaflypackaging-com/images/violetjar/background-1.jpg') repeat center center;  top: 0; left: 0;  width: 100%; height: 120vh; background-size: contain; background-position: center;"></div>
        </div>
    </div>

    <!-- 应用场景 -->
    <section  class="parallax-content-section relative overflow-hidden">
        <!-- 内容层：上半部分透明，下半部分有背景 -->
        <div class="parallax-content-wrapper relative z-10">
            <!-- 透明区域（上半部分） -->
            <div class="parallax-transparent-area" style="min-height: 100vh;"></div>
            
            <!-- 内容区域（下半部分，有白色背景） -->
            <div class="parallax-content-area bg-gray-50 py-20">
                <div class="container mx-auto px-4">
                    <div class="text-center mb-16" data-aos="fade-up">
                        <h2 class="text-4xl md:text-5xl mb-6 text-gray-900">
                            Perfect For Premium Products
                        </h2>
                        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
                            Trusted by leading brands worldwide for protecting their most valuable formulations
                        </p>
                    </div>
                    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div class="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos-delay="100">
                            <div class="flex items-center gap-3 mb-4">
                                <div class="text-4xl">🌿</div>
                                <h3 class="text-xl  text-gray-900">Cannabis & Hemp</h3>
                            </div>
                            <p class="text-gray-600">Preserve cannabinoids, terpenes, and potency for extended periods.</p>
                        </div>

                        <div class="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos-delay="200">
                            <div class="flex items-center gap-3 mb-4">
                                <div class="text-4xl">💊</div>
                                <h3 class="text-xl  text-gray-900">Supplements</h3>
                            </div>
                            <p class="text-gray-600">Maintain efficacy of vitamins, herbs, and nutritional products.</p>
                        </div>

                        <div class="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos-delay="300">
                            <div class="flex items-center gap-3 mb-4">
                                <div class="text-4xl">✨</div>
                                <h3 class="text-xl text-gray-900">Cosmetics</h3>
                            </div>
                            <p class="text-gray-600">Protect natural skincare and beauty formulations from degradation.</p>
                        </div>

                        <div class="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos-delay="400">
                            <div class="flex items-center gap-3 mb-4">
                                <div class="text-4xl">🍃</div>
                                <h3 class="text-xl text-gray-900">Essential Oils</h3>
                            </div>
                            <p class="text-gray-600">Preserve aromatic compounds and therapeutic properties.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- 产品展示区域 -->
    <section id="violet-glass-collection" class="py-20 bg-white">
        <div class="container mx-auto px-4">
            <div class="text-center mb-16" data-aos="fade-up">
                <h2 class="text-4xl md:text-5xl mb-6 text-gray-900">
                    Our Violet Glass Collection
                </h2>
                <p class="text-xl text-gray-600 max-w-3xl mx-auto">
                    Premium biophotonic glass containers designed for ultimate product protection
                </p>
            </div>

            <!-- 二级导航目录 -->
            <!-- <div v-if="navItemList.length > 0" ref="navContainerRef" class="custom-scrollbar-container flex justify-between mb-12 gap-4 overflow-x-auto text-center" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp" @mouseleave="handleMouseUp" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd" :class="{ 'is-dragging': isDragging }">
                <nuxt-link v-for="item in navItemList" :to="item.linkUrl" :key="item.id" class="flex-1 py-4 px-6 text-center text-lg font-semibold transition-all rounded-xl min-w-[150px] hover:text-violet-600 hover:border-violet-600" :class="item.linkUrl === route.path ? 'border-2 border-violet-600 text-violet-600 bg-violet-50' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-violet-50'" @click="handleLinkClick">
                    {{ item.name }}
                </nuxt-link>
            </div> -->
            
            <!-- 产品网格 -->
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                <div v-for="(article, index) in articlesPage.rows" :key="index" class="group bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300" data-aos="fade-up" :data-aos-delay="index * 50">
                    <!-- 产品图片 -->
                    <div class="relative w-full pt-[100%] bg-gradient-to-br from-violet-50 to-purple-50 overflow-hidden">
                        <img :src="cmsUtils.isEmpty(article.imageUrl) ? '/images/xxx.jpg' : article.imageUrl!.split(',')[0]" :alt="article.title" class="absolute top-0 left-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                        <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>

                    <!-- 产品信息 -->
                    <div class="p-4">
                        <div class="h-[122px]">
                            <h3 class=" line-clamp-2 text-gray-900 group-hover:text-violet-600 transition-colors font-weight-600">
                                <nuxt-link :to="article.fullUrl">{{ article.title }}</nuxt-link>
                            </h3>
                            <p class="mt-2 line-clamp-3  text-gray-600 text-sm ">{{ article.description }}</p>
                        </div>
                        <nuxt-link :to="article.fullUrl" class="mt-4 block w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white text-center py-3 rounded-xl text-sm font-semibold hover:from-violet-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-md">
                            View Details
                        </nuxt-link>
                    </div>
                </div>
            </div>

            <!-- 分页 -->
            <div class="flex justify-center mt-16">
                <page-icon :page-num="props.pageNum" :page-size="props.pageSize" :total="articlesPage.total!" />
            </div>
        </div>
    </section>

    <!-- 为什么选择紫罗兰玻璃 -->
    <section class="py-20 bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
        <div class="absolute inset-0 opacity-10">
            <div class="absolute top-0 left-0 w-96 h-96 bg-violet-500 rounded-full filter blur-3xl"></div>
            <div class="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
        </div>
        <div class="container mx-auto px-4 relative z-10">
            <div class="max-w-4xl mx-auto text-center" data-aos="fade-up">
                <h2 class="text-4xl md:text-5xl  mb-8">
                    Why Choose Violet Glass?
                </h2>
                <div class="grid md:grid-cols-2 gap-8 text-left">
                    <div class="bg-white/10 backdrop-blur-sm p-8 rounded-2xl">
                        <h3 class="text-2xl  mb-4">🔬 Scientifically Proven</h3>
                        <p class="text-lg opacity-90">
                            Independent laboratory tests confirm superior preservation compared to amber, green, or clear glass.
                        </p>
                    </div>
                    <div class="bg-white/10 backdrop-blur-sm p-8 rounded-2xl">
                        <h3 class="text-2xl mb-4">🌍 Sustainable Choice</h3>
                        <p class="text-lg opacity-90">
                            100% recyclable, reusable, and eco-friendly. Reduce waste while protecting your products.
                        </p>
                    </div>
                    <div class="bg-white/10 backdrop-blur-sm p-8 rounded-2xl">
                        <h3 class="text-2xl  mb-4">💎 Premium Aesthetics</h3>
                        <p class="text-lg opacity-90">
                            Distinctive violet color elevates brand perception and creates shelf appeal.
                        </p>
                    </div>
                    <div class="bg-white/10 backdrop-blur-sm p-8 rounded-2xl">
                        <h3 class="text-2xl  mb-4">📈 Market Advantage</h3>
                        <p class="text-lg opacity-90">
                            Stand out from competitors with packaging that demonstrates quality and innovation.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-white">
        <div class="container mx-auto px-4 text-center" data-aos="fade-up">
            <h2 class="text-4xl md:text-5xl  mb-6 text-gray-900">
                Ready to Elevate Your Brand?
            </h2>
            <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                Discover how violet glass can transform your product packaging and preservation
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <nuxt-link  href="/contact-us" class="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg">
                    Request Samples
                </nuxt-link>
            </div>
        </div>
    </section>

    <!-- 页脚 -->
    <footer-index />
</template>

<script setup lang="ts" name="CategoryVioletJar">
    import { ref, onMounted } from 'vue'
    import type { Category, NavItemVo, Tags, Article, Page } from '~/types';
    import headerLeafly from '../../components/header/header-leafly.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import pageIcon from '../../components/common/pageIcon.vue';
    import cmsConfig from '~/cmsConfig';
    import cmsUtils from '~/utils/cmsUtils';
    import AOS from 'aos';
    import 'aos/dist/aos.css';

    const route = useRoute()

    const cmsInfo = await cmsUtils.getCmsInfo()
    const navItemList = ref<NavItemVo[]>(cmsUtils.getNavItemTreeByNavIndex("index_category", cmsInfo) as NavItemVo[])

    // 拖拽滚动相关状态
    const navContainerRef = ref<HTMLElement | null>(null)
    const isDragging = ref(false)
    const startX = ref(0)
    const scrollLeft = ref(0)
    const hasMoved = ref(false)

    // 视频播放控制
    const videoRef = ref<HTMLVideoElement | null>(null)
    const showPlayButton = ref(true) // 初始显示播放按钮

    // 视频开始播放处理
    const onVideoPlay = () => {
        showPlayButton.value = false
    }

    // 视频播放结束处理
    const onVideoEnded = () => {
        showPlayButton.value = true
    }

    // 播放/重新播放视频
    const replayVideo = () => {
        if (videoRef.value) {
            // 如果视频已结束，从头开始播放
            if (videoRef.value.ended) {
                videoRef.value.currentTime = 0
            }
            videoRef.value.play()
        }
    }

    // 滚动到产品展示区域
    const scrollToCollection = () => {
        const targetElement = document.getElementById('violet-glass-collection')
        if (targetElement) {
            // 获取header高度，用于偏移（避免被固定header遮挡）
            const headerHeight = 100 // 可以根据实际header高度调整
            const targetPosition = targetElement.offsetTop - headerHeight
            
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            })
        }
    }

    // 触摸事件处理（移动端）
    const handleTouchStart = (e: TouchEvent) => {
        if (!navContainerRef.value || !e.touches[0]) return
        isDragging.value = true
        hasMoved.value = false
        startX.value = e.touches[0].pageX - navContainerRef.value.offsetLeft
        scrollLeft.value = navContainerRef.value.scrollLeft
    }

    const handleTouchMove = (e: TouchEvent) => {
        if (!isDragging.value || !navContainerRef.value || !e.touches[0]) return
        const x = e.touches[0].pageX - navContainerRef.value.offsetLeft
        const walk = (x - startX.value) * 2
        navContainerRef.value.scrollLeft = scrollLeft.value - walk
        hasMoved.value = true
    }

    const handleTouchEnd = () => {
        isDragging.value = false
    }

    // 处理链接点击
    const handleLinkClick = (e: MouseEvent) => {
        if (hasMoved.value) {
            e.preventDefault()
            e.stopPropagation()
            hasMoved.value = false
        }
    }

    // 接收分类数据
    const props = withDefaults(defineProps<{
        category: Category | null
        tagsList?: Tags[]
        pageSize?: number
        pageNum?: number
    }>(), {
        tagsList: () => [],
        pageSize: 12,
        pageNum: 1
    })

    const childCategoryIds = cmsUtils.getChildCategoryIds(props.category!.id, cmsInfo);
    const allCategoryIds = [props.category!.id, ...childCategoryIds];
    const body = {
        "websiteId": cmsInfo!.website.id,
        "categoryIds": allCategoryIds,
        "tagsIds": props.tagsList.map(item => item.id),
        "pageNum": props.pageNum,
        "pageSize": props.pageSize
    }

    const articlesPage = await useHttp("/nuxt/article/list", {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: body
    }) as Page<Article>;
    articlesPage.rows.forEach((article: Article) => {
        let articleCategory: Category = cmsInfo.category.find(item => item.id == article.categoryId)!;
        article.fullUrl = cmsUtils.appendCategoryUrl(articleCategory, cmsInfo, "/" + article.titleUrl);
    });

    // 初始化 AOS 动画
    onMounted(() => {
        AOS.init({
            duration: 800,
            easing: 'ease-in-out',
            once: true,
            offset: 100
        });
    });

    // JSON-LD 结构化数据（产品列表）
    const baseUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain
    const canonicalUrl = baseUrl + route.path.replace('.html','')
    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    itemListElement: articlesPage.rows.map((article, index) => ({
                        '@type': 'ListItem',
                        position: (props.pageNum - 1) * props.pageSize + index + 1,
                        item: {
                            '@type': 'Product',
                            name: article.title,
                            url: baseUrl + article.fullUrl,
                            ...(article.imageUrl ? { image: article.imageUrl.split(',')[0] } : {}),
                            ...(article.sku ? { sku: article.sku } : {}),
                            ...(article.description ? { description: article.description } : {})
                        }
                    })),
                    numberOfItems: articlesPage.total,
                    name: props.category?.title || props.category?.name,
                    ...(props.category?.description ? { description: props.category.description } : {}),
                    url: canonicalUrl
                })
            }
        ]
    })

</script>

<style scoped lang="scss">
/* Hero Section */
.violet-hero {
    position: relative;
    height: 600px;
    background-image: url('/leaflypackaging-com/images/violetjar/banner.jpg');
    background-size: cover;
    background-position: center;
    background-blend-mode: overlay;
    display: flex;
    align-items: center;
    
    @media (max-width: 768px) {
        height: 500px;
    }
}

.hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(91, 33, 182, 0.15) 0%, rgba(124, 58, 237, 0.15) 50%, rgba(168, 85, 247, 0.15) 100%);
    z-index: 1;
}

/* 文本截断样式 */
.line-clamp-2 {
    display: -webkit-box;
    -line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* 确保图片容器和内容区域有固定高度 */
.h-12 {
    height: 3rem;
}

.h-14 {
    height: 3.5rem;
}

/* 自定义滚动条样式 */
.custom-scrollbar-container {
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar {
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 3px;
        transition: background 0.3s ease;
    }

    &:hover {
        scrollbar-color: #7c3aed transparent;

        &::-webkit-scrollbar-thumb {
            background: #7c3aed;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #6d28d9;
        }
    }

    &.is-dragging {
        cursor: grabbing;
    }

    a {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
    }
}

.parallax-background-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height:100hv; /* 比容器高一些，确保滚动时有足够的图片显示 */
    background-color: #4d1d95;
    z-index: -1;
}


/* 视差滚动背景样式 */
.parallax-content-section {
    position: relative;
    width: 100%;
    overflow: hidden;
}


.parallax-background-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
}

.parallax-content-wrapper {
    position: relative;
    z-index: 1;
}

.parallax-transparent-area {
    background: transparent; /* 透明区域，可以看到背景图片 */
}

.parallax-content-area {
    position: relative;
    /* bg-gray-50 已经在class中设置了 */
}

/* 卡片悬停效果 */
.group:hover {
    transform: translateY(-4px);
}

/* 渐变按钮动画 */
button, a {
    transition: all 0.3s ease;
}

/* 视频播放按钮样式 */
.video-play-overlay {
    z-index: 9999;
}

.play-button-triangle {
    width: 0;
    height: 0;
    border-left: 40px solid white;
    border-top: 25px solid transparent;
    border-bottom: 25px solid transparent;
    margin-left: 8px;
    transition: transform 0.2s ease;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
}

.video-play-overlay:hover .play-button-triangle {
    transform: scale(1.1);
}

/* 响应式调整 */
@media (max-width: 640px) {
    .violet-hero h1 {
        font-size: 2.5rem;
    }
    
    .violet-hero p {
        font-size: 1.125rem;
    }

    .play-button-triangle {
        border-left-width: 30px;
        border-top-width: 18px;
        border-bottom-width: 18px;
        margin-left: 6px;
    }
    .leading-relaxed{
        line-height: 1.35rem;
    }
}
</style>
